import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  Headset,
  Loader2,
  MessageCircle,
  Plus,
  Trash2,
  Pencil,
  UserCheck,
} from "lucide-react";
import type { CreateSupportLinkInput, SupportLinkDto, UserRole } from "@mvk/shared";
import { useAdminEvent } from "../hooks/useAdminEvent";
import { useAdminAuth } from "../auth/AdminAuthContext";
import { supportLinksService } from "../../services/supportLinksService";
import {
  supportEscalationsService,
  type SupportEscalationDetail,
  type SupportEscalationSummary,
} from "../../services/supportEscalationsService";
import { isApiEnabled } from "../../api/config";
import { ApiError } from "../../api/client";

const LINK_TYPES: { id: CreateSupportLinkInput["linkType"]; label: string }[] = [
  { id: "tech", label: "ИИ-помощник" },
  { id: "general", label: "Общие вопросы" },
  { id: "logistics", label: "Логистика" },
  { id: "exhibitor", label: "Экспоненты" },
  { id: "organizer", label: "Организаторы" },
];

const ALL_ROLES: UserRole[] = ["visitor", "exhibitor", "organizer", "moderator"];

const EMPTY_FORM: CreateSupportLinkInput = {
  linkType: "tech",
  title: "",
  description: "",
  icon: "HelpCircle",
  url: "",
  openMode: "external",
  roles: ["visitor"],
  sortOrder: 0,
  enabled: true,
};

type Tab = "inbox" | "links";

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function AdminSupportLinksPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAdminAuth();
  const { event, loading: eventLoading } = useAdminEvent(id);

  const [tab, setTab] = useState<Tab>("inbox");
  const [links, setLinks] = useState<SupportLinkDto[]>([]);
  const [escalations, setEscalations] = useState<SupportEscalationSummary[]>([]);
  const [inboxFilter, setInboxFilter] = useState<"open" | "done" | "all">("open");
  const [selected, setSelected] = useState<SupportEscalationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateSupportLinkInput>(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const deepLinkSession = searchParams.get("session");

  const loadLinks = useCallback(async () => {
    if (!id) return;
    const items = await supportLinksService.listForManage(id);
    setLinks(items);
  }, [id]);

  const loadInbox = useCallback(async () => {
    if (!id) return;
    const items = await supportEscalationsService.list(id, inboxFilter);
    setEscalations(items);
  }, [id, inboxFilter]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      if (tab === "links") await loadLinks();
      else await loadInbox();
    } catch {
      setError(tab === "links" ? "Не удалось загрузить ссылки" : "Не удалось загрузить обращения");
    } finally {
      setLoading(false);
    }
  }, [id, tab, loadLinks, loadInbox]);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep claim badges fresh while several admins watch the inbox.
  useEffect(() => {
    if (tab !== "inbox" || !id) return;
    const sessionId = selected?.sessionId ?? null;
    const t = window.setInterval(() => {
      void loadInbox().catch(() => {});
      if (sessionId) {
        void supportEscalationsService
          .get(id, sessionId)
          .then((detail) => {
            if (detail) setSelected(detail);
          })
          .catch(() => {});
      }
    }, 8000);
    return () => window.clearInterval(t);
  }, [tab, id, loadInbox, selected?.sessionId]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    setError("");
  };

  const openEdit = (link: SupportLinkDto) => {
    setEditingId(link.id);
    setForm({
      linkType: link.linkType,
      title: link.title,
      description: link.description,
      icon: link.icon,
      url: link.url,
      openMode: link.openMode,
      roles: [...link.roles],
      sortOrder: link.sortOrder,
      enabled: link.enabled,
    });
    setShowForm(true);
    setError("");
  };

  const toggleRole = (role: UserRole) => {
    setForm((prev) => {
      const roles = prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role];
      return { ...prev, roles: roles.length ? roles : [role] };
    });
  };

  const handleSave = async () => {
    if (!id || !form.title.trim() || !form.url.trim()) {
      setError("Заполните название и URL");
      return;
    }
    if (!isApiEnabled()) {
      setError("API отключён — настройка ссылок только через API");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await supportLinksService.update(id, editingId, form);
      } else {
        await supportLinksService.create(id, form);
      }
      setShowForm(false);
      await loadLinks();
    } catch {
      setError("Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (linkId: string) => {
    if (!id || !window.confirm("Удалить ссылку?")) return;
    try {
      await supportLinksService.remove(id, linkId);
      await loadLinks();
    } catch {
      setError("Не удалось удалить");
    }
  };

  const openEscalation = useCallback(async (sessionId: string) => {
    if (!id) return;
    setTab("inbox");
    setError("");
    try {
      let detail = await supportEscalationsService.get(id, sessionId);
      if (!detail) {
        setError("Не удалось открыть диалог");
        return;
      }
      // Free ticket → take into work so others see the assignee.
      if (!detail.claimedByUserId) {
        try {
          detail = await supportEscalationsService.claim(id, sessionId, false);
        } catch (err) {
          if (err instanceof ApiError && err.status === 409) {
            detail = (await supportEscalationsService.get(id, sessionId)) ?? detail;
          } else {
            throw err;
          }
        }
      }
      setSelected(detail);
      setReplyText("");
      await loadInbox();
    } catch {
      setError("Не удалось открыть диалог");
    }
  }, [id, loadInbox]);

  // Deep-link from header notifications: /support?session=…
  useEffect(() => {
    if (!id || !deepLinkSession) return;
    let cancelled = false;
    void (async () => {
      await openEscalation(deepLinkSession);
      if (cancelled) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("session");
          return next;
        },
        { replace: true },
      );
    })();
    return () => {
      cancelled = true;
    };
    // Only react to the session query param, not every searchParams change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deepLinkSession is the trigger
  }, [id, deepLinkSession, openEscalation, setSearchParams]);

  const takeEscalation = async (force = false) => {
    if (!id || !selected || claiming) return;
    setClaiming(true);
    setError("");
    try {
      const detail = await supportEscalationsService.claim(id, selected.sessionId, force);
      setSelected(detail);
      await loadInbox();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const detail = await supportEscalationsService.get(id, selected.sessionId);
        if (detail) setSelected(detail);
        setError(
          `Диалог уже взял ${detail?.claimedByName || "другой администратор"}`,
        );
      } else {
        setError("Не удалось взять диалог");
      }
    } finally {
      setClaiming(false);
    }
  };

  const releaseEscalation = async () => {
    if (!id || !selected || claiming) return;
    setClaiming(true);
    setError("");
    try {
      const detail = await supportEscalationsService.release(id, selected.sessionId, false);
      setSelected(detail);
      await loadInbox();
    } catch {
      setError("Не удалось освободить диалог");
    } finally {
      setClaiming(false);
    }
  };

  const markDone = async (sessionId: string) => {
    if (!id) return;
    try {
      await supportEscalationsService.setStatus(id, sessionId, "done");
      setSelected(null);
      await loadInbox();
    } catch {
      setError("Не удалось обновить статус");
    }
  };

  const sendReply = async () => {
    if (!id || !selected || !replyText.trim() || replying) return;
    const claimedByOther =
      selected.claimedByUserId &&
      user?.id &&
      selected.claimedByUserId !== user.id;
    if (claimedByOther) {
      const ok = window.confirm(
        `Диалог взял ${selected.claimedByName || "другой администратор"}. Всё равно ответить?`,
      );
      if (!ok) return;
    }
    setReplying(true);
    setError("");
    try {
      const detail = await supportEscalationsService.reply(
        id,
        selected.sessionId,
        replyText.trim(),
      );
      if (detail) {
        setSelected(detail);
        setReplyText("");
      }
      await loadInbox();
    } catch {
      setError("Не удалось отправить ответ");
    } finally {
      setReplying(false);
    }
  };

  const mineClaim =
    Boolean(selected?.claimedByUserId) && selected?.claimedByUserId === user?.id;
  const otherClaim =
    Boolean(selected?.claimedByUserId) && selected?.claimedByUserId !== user?.id;

  if (eventLoading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (!event) {
    return <div className="p-6 text-gray-500">Мероприятие не найдено</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/events/${id}`)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
          type="button"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[18px] text-gray-900">Поддержка</h1>
          <p className="text-[12px] text-gray-400 truncate">{event.title}</p>
        </div>
        {tab === "links" ? (
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 text-white text-[13px] hover:bg-indigo-700"
          >
            <Plus size={14} /> Добавить
          </button>
        ) : null}
      </div>

      <div className="flex gap-1 p-1 rounded-xl bg-gray-100">
        <button
          type="button"
          onClick={() => {
            setTab("inbox");
            setShowForm(false);
            setSelected(null);
          }}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[13px] ${
            tab === "inbox" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
          }`}
        >
          <Headset size={14} /> Обращения
        </button>
        <button
          type="button"
          onClick={() => {
            setTab("links");
            setSelected(null);
          }}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[13px] ${
            tab === "links" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
          }`}
        >
          <MessageCircle size={14} /> Ссылки
        </button>
      </div>

      {error && <p className="text-[13px] text-red-600">{error}</p>}

      {tab === "inbox" ? (
        <>
          <p className="text-[12px] text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Запросы из профиля «Помощник» (чип «Связь с менеджером» / «позови человека»).
            Ответ из этого экрана приходит пользователю в тот же чат помощника.
          </p>
          <div className="flex gap-2">
            {(["open", "done", "all"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setInboxFilter(f)}
                className={`px-2.5 py-1 rounded-full text-[11px] border ${
                  inboxFilter === f
                    ? "bg-indigo-100 border-indigo-200 text-indigo-700"
                    : "border-gray-200 text-gray-500"
                }`}
              >
                {f === "open" ? "Открытые" : f === "done" ? "Закрытые" : "Все"}
              </button>
            ))}
          </div>

          {selected ? (
            <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[15px] text-gray-900">{selected.userName}</p>
                  <p className="text-[12px] text-gray-500">
                    {selected.phone || "телефон не указан"} · {formatWhen(selected.escalatedAt)}
                  </p>
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  {selected.status === "open" ? (
                    <button
                      type="button"
                      onClick={() => void markDone(selected.sessionId)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-[12px]"
                    >
                      <CheckCircle2 size={12} /> Закрыть
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-[12px] text-gray-600"
                  >
                    К списку
                  </button>
                </div>
              </div>

              {otherClaim ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2">
                  <p className="text-[12px] text-sky-900 flex-1">
                    <UserCheck size={13} className="inline mr-1 -mt-0.5" />
                    Диалог взял {selected.claimedByName || "другой администратор"}
                    {selected.claimedAt ? ` · ${formatWhen(selected.claimedAt)}` : ""}
                  </p>
                  <button
                    type="button"
                    disabled={claiming}
                    onClick={() => void takeEscalation(true)}
                    className="px-2.5 py-1.5 rounded-lg bg-white border border-sky-200 text-sky-800 text-[12px] disabled:opacity-50"
                  >
                    {claiming ? "…" : "Перехватить"}
                  </button>
                </div>
              ) : mineClaim ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <p className="text-[12px] text-emerald-900 flex-1">
                    <UserCheck size={13} className="inline mr-1 -mt-0.5" />
                    Диалог у вас в работе
                  </p>
                  <button
                    type="button"
                    disabled={claiming}
                    onClick={() => void releaseEscalation()}
                    className="px-2.5 py-1.5 rounded-lg bg-white border border-emerald-200 text-emerald-800 text-[12px] disabled:opacity-50"
                  >
                    Освободить
                  </button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="text-[12px] text-amber-900 flex-1">Диалог свободен</p>
                  <button
                    type="button"
                    disabled={claiming}
                    onClick={() => void takeEscalation(false)}
                    className="px-2.5 py-1.5 rounded-lg bg-amber-600 text-white text-[12px] disabled:opacity-50"
                  >
                    {claiming ? "…" : "Взять в работу"}
                  </button>
                </div>
              )}

              {selected.pendingRequest ? (
                <p className="text-[12px] text-amber-900 bg-amber-50 rounded-lg px-3 py-2">
                  Запрос: {selected.pendingRequest}
                </p>
              ) : null}
              <div className="max-h-[50vh] overflow-y-auto space-y-2 border-t border-gray-50 pt-3">
                {selected.messages.map((m) => {
                  const isManager = m.meta?.kind === "manager_reply" || m.meta?.fromAdmin === true;
                  return (
                    <div
                      key={m.id}
                      className={`text-[13px] leading-snug rounded-lg px-3 py-2 ${
                        m.role === "user"
                          ? "bg-indigo-50 text-indigo-950 ml-6"
                          : isManager
                            ? "bg-emerald-50 text-emerald-950 mr-6"
                            : "bg-gray-50 text-gray-800 mr-6"
                      }`}
                    >
                      <p className="text-[10px] text-gray-400 mb-0.5">
                        {m.role === "user"
                          ? "Пользователь"
                          : isManager
                            ? "Менеджер"
                            : "Помощник"}{" "}
                        · {formatWhen(m.createdAt)}
                      </p>
                      <p className="whitespace-pre-wrap">{m.content}</p>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-gray-100 pt-3 space-y-2">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={3}
                  placeholder="Ответ пользователю в чат помощника…"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] resize-y min-h-[72px]"
                />
                <button
                  type="button"
                  disabled={replying || !replyText.trim()}
                  onClick={() => void sendReply()}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-indigo-600 text-white text-[13px] disabled:opacity-50"
                >
                  {replying ? "Отправка…" : "Отправить в чат"}
                </button>
              </div>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-10 text-gray-400">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {escalations.map((item) => {
                const takenByMe = item.claimedByUserId && item.claimedByUserId === user?.id;
                const takenByOther =
                  item.claimedByUserId && item.claimedByUserId !== user?.id;
                return (
                  <button
                    key={item.sessionId}
                    type="button"
                    onClick={() => void openEscalation(item.sessionId)}
                    className={`w-full text-left bg-white rounded-xl border p-4 transition-colors ${
                      takenByOther
                        ? "border-sky-200 hover:border-sky-300"
                        : takenByMe
                          ? "border-emerald-200 hover:border-emerald-300"
                          : "border-gray-100 hover:border-indigo-200"
                    }`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.eventTitle ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-100 truncate max-w-[12rem]">
                          {item.eventTitle}
                        </span>
                      ) : null}
                      <span className="text-[14px] text-gray-900">{item.userName}</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded ${
                          item.status === "open"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {item.status === "open" ? "открыто" : "закрыто"}
                      </span>
                      {takenByMe ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                          у вас в работе
                        </span>
                      ) : takenByOther ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-800">
                          взял {item.claimedByName || "админ"}
                        </span>
                      ) : item.status === "open" ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                          свободен
                        </span>
                      ) : null}
                    </div>
                    <p className="text-[12px] text-gray-600 mt-1 line-clamp-2">
                      {item.pendingRequest || item.lastUserMessage || "—"}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      {item.phone || "без телефона"} ·{" "}
                      {formatWhen(item.escalatedAt || item.updatedAt)}
                    </p>
                  </button>
                );
              })}
              {escalations.length === 0 && (
                <p className="text-center text-[13px] text-gray-400 py-8">
                  Пока нет обращений «позови человека»
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <p className="text-[12px] text-gray-500 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
            Внешние ссылки (Telegram, MAX и др.). Пользователь видит каналы по своей роли.
          </p>

          {showForm && (
            <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
              <h2 className="text-[14px] text-gray-800">
                {editingId ? "Редактировать ссылку" : "Новая ссылка"}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] text-gray-500">Тип</span>
                  <select
                    value={form.linkType}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        linkType: e.target.value as CreateSupportLinkInput["linkType"],
                      }))
                    }
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px]"
                  >
                    {LINK_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[11px] text-gray-500">Порядок</span>
                  <input
                    type="number"
                    value={form.sortOrder ?? 0}
                    onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px]"
                  />
                </label>
              </div>
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Название"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px]"
              />
              <input
                value={form.description ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Описание"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px]"
              />
              <input
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="https://t.me/..."
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px]"
              />
              <div>
                <span className="text-[11px] text-gray-500">Роли</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ALL_ROLES.map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => toggleRole(role)}
                      className={`px-2.5 py-1 rounded-full text-[11px] border ${
                        form.roles.includes(role)
                          ? "bg-indigo-100 border-indigo-200 text-indigo-700"
                          : "border-gray-200 text-gray-500"
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-[13px] text-gray-600">
                <input
                  type="checkbox"
                  checked={form.enabled !== false}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                />
                Активна
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-[13px] disabled:opacity-60"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : "Сохранить"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-[13px] text-gray-600"
                >
                  Отмена
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-10 text-gray-400">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {links.map((link) => (
                <div
                  key={link.id}
                  className={`bg-white rounded-xl border p-4 flex items-start gap-3 ${
                    link.enabled ? "border-gray-100" : "border-gray-100 opacity-60"
                  }`}
                >
                  <MessageCircle size={18} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[14px] text-gray-900">{link.title}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                        {link.linkType}
                      </span>
                      {!link.enabled && (
                        <span className="text-[10px] text-amber-600">выкл.</span>
                      )}
                    </div>
                    <p className="text-[12px] text-gray-500 mt-0.5 truncate">{link.url}</p>
                    <p className="text-[11px] text-gray-400 mt-1">Роли: {link.roles.join(", ")}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => supportLinksService.openLink(link)}
                      className="p-1.5 rounded-lg hover:bg-gray-50 text-gray-400"
                      title="Открыть"
                    >
                      <ExternalLink size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(link)}
                      className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-500"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(link.id)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {links.length === 0 && (
                <p className="text-center text-[13px] text-gray-400 py-8">Нет ссылок</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
